import { ByteReader, ByteWriter, snap } from "@obean/sim-core";

/**
 * Binary frames, server → client (ADR 0011): `u8 kind | varuint frameVersion | body`. Bodies are
 * `snap` structs, so fields can be appended without breaking older clients (they skip them) or
 * newer ones (defaults fill them in). An unknown kind comes from a newer server; clients skip it.
 */
export const FrameKind = { snapshot: 1 } as const;

/** Upper bound for one game snapshot. A tank snapshot is about 1 KB; this bounds a corrupt frame. */
export const MAX_GAME_SNAPSHOT_BYTES = 64 * 1024;

const SNAPSHOT_FRAME_VERSION = 1;

export interface SnapshotFrame {
  /** Server tick of this state (interpolation runs on tick time, P2-SIM-03). */
  tick: number;
  /** Last input seq from THIS client that the simulation has applied (for reconciliation). */
  ack: number;
  /** The game's encodeSnapshot bytes. */
  game: Uint8Array;
}

const snapshotBody = snap.struct({
  tick: snap.u32(),
  ack: snap.u32(),
  game: snap.bytes({ max: MAX_GAME_SNAPSHOT_BYTES }),
});

/** Server side. The game bytes are encoded once per tick; this header is per client (its ack). */
export function encodeSnapshotFrame(frame: SnapshotFrame): Uint8Array {
  const w = new ByteWriter(16 + frame.game.length);
  w.u8(FrameKind.snapshot);
  w.varuint(SNAPSHOT_FRAME_VERSION);
  snapshotBody.write(w, frame);
  return w.finish();
}

export type ServerFrame =
  | { kind: "snapshot"; frame: SnapshotFrame }
  /** From a newer server: skip it. */
  | { kind: "unknown"; id: number }
  | { kind: "invalid"; error: string };

/** Client side, for binary frames. Never throws. */
export function decodeServerFrame(data: ArrayBuffer | Uint8Array): ServerFrame {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  try {
    const r = new ByteReader(bytes);
    const kind = r.u8();
    if (kind !== FrameKind.snapshot) return { kind: "unknown", id: kind };
    const version = r.varuint();
    const frame = snapshotBody.read(r, version);
    if (r.position !== r.end) return { kind: "invalid", error: "trailing bytes" };
    return { kind: "snapshot", frame };
  } catch (err) {
    return { kind: "invalid", error: String(err) };
  }
}
